
import {useEffect, useState} from 'react';
import {Tooltip, Spin, Popover, Divider} from 'antd';
import styles from '../index.module.scss';
import {useStore} from '@/store/root-store';
import {CommonInput} from '@/components/common-components/components';
import {Button as V2Button} from '@/components/common-components/v2';
import styled from 'styled-components';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {LoadingOutlined} from '@ant-design/icons';
import {faCircleCheck, faHexagonExclamation, faTriangleExclamation, faArrowLeft} from '@fortawesome/pro-solid-svg-icons';
import {faArrowsRotate} from '@fortawesome/pro-regular-svg-icons';
import {MetaIdeasList} from './MetaIdeasList';
import {observer} from 'mobx-react';
import {CaAiQuotaInfo} from '@/components/common-components/v2/CaAiQuotaInfo';


export const MetaTagGenerator = observer(() => {
  const {contentOptimizer: {
    currentPage: {
      content,
      isPublic,
      isExportingPage,
      updateMetaData,
      setIsExternalTitleEdit,
      feTargetKeywords,
      isMetaUpdating,
      editTitle,
    },
    aiMetaData: {
      loading,
      setLoading,
      loadAiMetaData,
      metaData,
    },
  },
  settings: {customer: {getCustomerQuota}},
  } = useStore('');

  const [showMetaDataModal, setShowMetaDataModal] = useState(false);
  const [customMetaTitle, setCustomMetaTitle] = useState('');
  const [customMetaDesc, setCustomMetaDesc] = useState(content.metaDesc || '');
  const [showAiMetaResults, setShowAiMetaResults] = useState(false);


  const antdIcon = <LoadingOutlined style={{fontSize: 14, marginBottom: 2, color: '#2D6CCA'}} spin />;

  useEffect(()=>{
    if (content?.title) {
      setCustomMetaTitle(content?.title);
    }
    setCustomMetaDesc(content.metaDesc || metaData[0]?.metaDesc);
    async function updateMetadata() {
      if ( !content?.metaTitle && metaData?.length) {
        await updateMetaData({meta_title: metaData[0]?.metaTitle, meta_desc: metaData[0]?.metaDesc});
      }
    }
    updateMetadata();
  }, [content.metaTitle, metaData.length, showMetaDataModal]);

  const checkPageTitle = (title: string): boolean => {
    const regexp = new RegExp('^New Document');
    if (!title || regexp.test(title)) {
      return false;
    } else {
      return true;
    }
  };

  const useAiMetaHandler = (title, desc) => {
    if (title) setCustomMetaTitle(title.trim());
    if (desc) setCustomMetaDesc(desc.trim());
    setShowAiMetaResults(false);
  };


  useEffect(() => {
    setCustomMetaTitle(content.metaTitle);
    setCustomMetaDesc(content.metaDesc);
  }, []);

  const aiButtonTooltipMsg = () => {
    let msg = '';
    if (!feTargetKeywords?.length && !checkPageTitle(content.title)) {
      msg = 'Please add a custom page title and at least 1 target keyword to generate AI Meta Data suggestions';
    }

    if (feTargetKeywords?.length && !checkPageTitle(content.title)) {
      msg = 'Please add a custom page title to generate AI Meta Data suggestions';
    }

    if (!feTargetKeywords?.length && checkPageTitle(content.title)) {
      msg = 'Please add at least 1 target keyword to generate AI Meta Data suggestions';
    }

    return msg;
  };

  /*
  * Dynamically return appropriate icon for main Meta Data title, which will emit if number of characters matches expected
  */
  const generateMetaDescIcon = () => {
    // if either metaTItle or metaDesc is missing
    if (!customMetaTitle || !customMetaDesc) return <FontAwesomeIcon icon={faHexagonExclamation} color='#F44343' style={{marginRight: 5}}/>;

    const metaDescLengthMin = 50;
    const metaDescLengthMax = 160;
    const metaTitleLengthMin = 30;
    const metaTitleLengthMax = 60;

    // if both metaTItle or metaDesc exist and at least one of them doesnt fint recomended length
    if (customMetaDesc.length < metaDescLengthMin || customMetaDesc.length > metaDescLengthMax) return <FontAwesomeIcon icon={faTriangleExclamation} color='#FF8536' style={{marginRight: 5}}/>;
    if (customMetaTitle.length < metaTitleLengthMin || customMetaTitle.length > metaTitleLengthMax) return <FontAwesomeIcon icon={faTriangleExclamation} color='#FF8536' style={{marginRight: 5}}/>;

    // if both metaTItle or metaDesc exist and both match/exceed recommended length
    return <FontAwesomeIcon icon={faCircleCheck} color='#1FAC47' style={{marginRight: 5}}/>;
  };


  /*
  * Dynamically return appropriate icon for both Meta Desc and Meta Title, which will emit if number of characters matches expected
  */
  const generateSingleMetaIcon = (metaKey: 'metaTitle' | 'metaDesc', metaValue?: string) => {
    const label = metaKey === 'metaTitle' ? 'meta title' : 'meta description';

    // if meta doesnt exist
    if (!metaValue) {
      return <Tooltip title={`Please add a ${label} to your website.`}>
        <FontAwesomeIcon icon={faHexagonExclamation} color='#F44343' style={{marginLeft: 5, marginTop: 1}}/>
      </Tooltip>;
    }

    const metaValueMinLength = metaKey === 'metaTitle' ? 30 : 50;
    const metaValueMaxLength = metaKey === 'metaTitle' ? 60 : 160;


    const largerThenMax = metaValue.length > metaValueMaxLength;
    const smallerThanMin = metaValue.length < metaValueMinLength;
    const idealSize = metaValue.length >= metaValueMinLength && metaValue.length <= metaValueMaxLength;
    const missing = !metaValue?.length;
    let largerBy = 0;
    let smallerBy = 0;

    if (smallerThanMin) smallerBy = metaValueMinLength - metaValue.length;
    if (largerThenMax) largerBy = metaValue.length - metaValueMaxLength;

    let statusMsg = '';
    if (idealSize) statusMsg = 'No issues found.';
    if (largerThenMax) statusMsg = `Consider lowering the length of the page's ${label} tag by ${largerBy} characters`;
    if (smallerThanMin) statusMsg = `Consider increasing the length of the page's ${label} tag by ${smallerBy} characters`;
    if (missing) statusMsg = `Please add a ${label} to your website.`;


    // if meta exists but its value is less then suggested length
    if (metaValue.length < metaValueMinLength || metaValue.length > metaValueMaxLength) {
      return <Tooltip title={statusMsg}>
        <FontAwesomeIcon icon={faTriangleExclamation} color='#FF8536' style={{marginLeft: 5, marginTop: 1}}/>
      </Tooltip>;
    }

    // if meta exists and value matches or exceeds suggested length
    return <Tooltip title={statusMsg}>
      <FontAwesomeIcon icon={faCircleCheck} color='#1FAC47' style={{marginLeft: 5, marginTop: 1}}/>
    </Tooltip>;
  };


  const updateMetaDataHandler = async (title, desc) => {
    const payload = {
      'meta_title': title,
      'meta_desc': desc,
    };


    setIsExternalTitleEdit(false);
    try {
      await editTitle(title);
      await updateMetaData(payload);
      setIsExternalTitleEdit(true);
    } catch (e) {
      return Promise.reject(e);
    } finally {
      setIsExternalTitleEdit(false);
    }
    setShowMetaDataModal(false);
  };

  const aiMetaDataHandler = async () => {
    const targetKwArr = feTargetKeywords.map(kw => kw.keyword);
    setLoading(true);
    try {
      const response = await loadAiMetaData(content.title, targetKwArr);
      response && setShowAiMetaResults(true);
      setLoading(false);
      await getCustomerQuota();
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const aiSuggestionMsg = metaData.length ? <><span>AI Suggestions Generated</span>
    : {(!feTargetKeywords?.length || !checkPageTitle(content.title)) ? <Tooltip title={aiButtonTooltipMsg()}>
      <FontAwesomeIcon icon={faArrowsRotate} style={{color: '#2D6CCA', marginLeft: 5, cursor: 'not-allowed'}}/>
    </Tooltip> :
      <FontAwesomeIcon icon={faArrowsRotate} onClick={() => aiMetaDataHandler()} style={{color: '#2D6CCA', marginLeft: 5, cursor: 'pointer'}}/>}</> :
    'Our AI can generate Meta Data based on your content.';


  const metaDataTooltipContent = showAiMetaResults ? (
    <>
      <div style={{fontWeight: 500, cursor: 'pointer', marginBottom: 16}} onClick={() => setShowAiMetaResults(false)}><FontAwesomeIcon icon={faArrowLeft}/> Back to Meta Details</div>
      <MetaIdeasList metaIdeas={metaData} onIdeaUse={useAiMetaHandler}/>
    </>
  ):(
    <>
      <div style={{display: 'flex', justifyContent: 'space-between'}}>
        <div style={{fontWeight: 500}}>Meta Data {loading && <Spin indicator={antdIcon} />}</div>
        <div style={{marginRight: 8}}><CaAiQuotaInfo /></div>
      </div>

      <div style={{display: 'flex', justifyContent: 'space-between'}}>
        <div style={{width: 190, fontSize: 12}}>{aiSuggestionMsg}</div>
        {metaData.length ?
          <div onClick={() => setShowAiMetaResults(true)} style={{color: '#2D6CCA', fontSize: 12, cursor: 'pointer', marginRight: 14}}>View all</div> :
          (!feTargetKeywords?.length && !checkPageTitle(content.title)) ? <Tooltip title={aiButtonTooltipMsg()}>
            <V2Button color='blue' variant='ghost' style={{cursor: 'not-allowed'}}>AI Generate</V2Button>
          </Tooltip> :
            <V2Button color='blue' variant='ghost' onClick={() => aiMetaDataHandler()} disabled={loading}>AI Generate</V2Button> }
      </div>
      <Divider style={{margin: '10px -16px', width: 'calc(100% + 32px)'}}/>
      <div style={{marginBottom: 17}}>
        <div style={{display: 'flex'}}>
          <div style={{fontSize: 12, marginBottom: 5}}>Meta Title</div>{generateSingleMetaIcon('metaTitle', customMetaTitle)}
        </div>
        <CommonInput
          disabled={isMetaUpdating}
          inputType='text'
          placeholder={'SEO For Enterprise Level Companies'}
          value={customMetaTitle}
          onChange={e => {
            setCustomMetaTitle(e.target.value);
          }} />
      </div>
      <div style={{marginBottom: 17}}>
        <div style={{display: 'flex'}}>
          <div style={{fontSize: 12, marginBottom: 5}}>Meta Description</div>{generateSingleMetaIcon('metaDesc', customMetaDesc)}
        </div>
        <CommonInput
          disabled={isMetaUpdating}
          inputType='textarea'
          placeholder={'e.g “Coffee brewing companies in New York”'}
          value={customMetaDesc}
          onChange={e => {
            setCustomMetaDesc(e.target.value);
          }}/>
      </div>

      <V2Button onClick={() => updateMetaDataHandler(customMetaTitle, customMetaDesc)} style={{marginLeft: 0}} disabled={isMetaUpdating}>
        Update Meta
      </V2Button>
    </>
  );

  return !isPublic && <Popover
    destroyTooltipOnHide={true}
    overlayInnerStyle={{borderRadius: 8}}
    content={metaDataTooltipContent}
    visible={showMetaDataModal}
    overlayClassName={styles.metaDescPopup}
    placement='bottom'
    trigger='click'
    onVisibleChange={() => {
      // setCustomMetaTitle(content?.metaTitle);
      // setCustomMetaDesc(content.metaDesc);
      if (showMetaDataModal) {
        setShowMetaDataModal(false);
      }
    }}>
    <MetaDataBtnStyled
      variant='ghost'
      color='textGrayDark'
      textColor='#000'
      style={{cursor: 'pointer', display: isExportingPage ? 'none' : 'block', marginRight: 4}}
      disabled={loading}
      onClick={() => {
        if (!showMetaDataModal) {
          setShowMetaDataModal(true);
        } else {
          setShowMetaDataModal(false);
        }
      }}>
      {!loading ? generateMetaDescIcon() : <Spin indicator={antdIcon} />} Meta Data
    </MetaDataBtnStyled>
  </Popover>;
});


const MetaDataBtnStyled = styled(V2Button)`
  height: 22px;
  padding: 1px 8px 2px 8px;
  margin-left: 20px;
  font-size: 12px;
`;

